import { Controller } from "@hotwired/stimulus";
const MAX_FIELDS = { previousEventData: 10, event_asts: 4 };
// Connects to data-controller="add-fields"
export default class extends Controller {
  static targets = ["fieldContainer", "fieldRow"];

  addEventAstField(event) {
    event.preventDefault();
    this.addField("event_asts", () => this.eventAstTemplate());
  }

  addPreviousEventDataField(event) {
    event.preventDefault();
    this.addField("previousEventData", this.previousEventDataTemplate);
  }

  addContactField(event) {
    event.preventDefault();
    this.addField("contact", this.contactTemplate);
  }

  addInviteeField(event) {
    event.preventDefault();
    this.addField("invitee", () => this.inviteeTemplate());
  }

  addConferenceDatesField(event) {
    event.preventDefault();
    this.addField("conference_dates", this.conferenceDatesTemplate);
  }

  addExhibitingDatesField(event) {
    event.preventDefault();
    this.addField("exhibiting_dates", this.exhibitingDatesTemplate);
  }

  addAdditionalPropertyField(event) {
    event.preventDefault();
    this.addField("additional_properties", this.additionalPropertyTemplate);
  }

  addHighValuePropertyField(event) {
    event.preventDefault();
    this.addField("high_value_property_items", this.highValuePropertyTemplate);
  }

  addPersonalCheckingField(event) {
    event.preventDefault();
    this.addField("personal_checking_accounts", () =>
      this.bankAccountTemplate("personal_checking"),
    );
  }

  addPersonalSavingsField(event) {
    event.preventDefault();
    this.addField("personal_savings_accounts", () =>
      this.bankAccountTemplate("personal_savings"),
    );
  }

  addBusinessCheckingField(event) {
    event.preventDefault();
    this.addField("business_checking_accounts", () =>
      this.bankAccountTemplate("business_checking"),
    );
  }

  addBusinessSavingsField(event) {
    event.preventDefault();
    this.addField("business_savings_accounts", () =>
      this.bankAccountTemplate("business_savings"),
    );
  }

  addNonRetirementInvestmentField(event) {
    event.preventDefault();
    this.addField(
      "non_retirement_investments",
      this.nonRetirementInvestmentTemplate,
    );
  }

  addRetirementPlanField(event) {
    event.preventDefault();
    this.addField("retirement_plans", this.retirementPlanTemplate);
  }

  addLifeInsuranceField(event) {
    event.preventDefault();
    this.addField("life_insurance", this.lifeInsuranceTemplate);
  }

  addHighRiskVehicleField(event) {
    event.preventDefault();
    this.addField("high_risk_vehicles", this.highRiskVehicleTemplate);
  }

  addPersonalVehicleField(event) {
    event.preventDefault();
    this.addField("personal_vehicles", this.personalVehicleTemplate);
  }

  addBusinessOwnershipField(event) {
    event.preventDefault();
    this.addField("business_ownership", this.businessOwnershipTemplate);
  }

  removeField(event) {
    event.preventDefault();

    const row = event.target.closest("[data-add-fields-target='fieldRow']");

    const container = row.closest("[data-add-fields-target='fieldContainer']");

    // Nested attributes for ASTs
    const destroyInput = row.querySelector("input[name*='[_destroy]']");
    const idInput = row.querySelector("input[name*='[id]']");

    if (destroyInput && idInput) {
      destroyInput.value = "true";
      row.style.display = "none";
    } else {
      row.remove();
    }

    if (container) this.updateRequiredFields(container);
  }

  addField(type, template) {
    const container = this.fieldContainerTargets.find(
      (c) => c.dataset.addFieldsContainerType === type,
    );
    if (!container) return;

    const max = MAX_FIELDS[type];
    if (max) {
      const allRows = container.querySelectorAll(
        "[data-add-fields-target='fieldRow']",
      );

      const visibleCount = Array.from(allRows).filter((row) => {
        const style = window.getComputedStyle(row);
        return style.display !== "none";
      }).length;

      if (visibleCount >= max) return;
    }

    const newRow = document.createElement("div");
    newRow.innerHTML = template();
    container.appendChild(newRow.firstElementChild);
    this.updateRequiredFields(container);
  }

  updateRequiredFields(container) {
    const rows = container.querySelectorAll(
      "[data-add-fields-target='fieldRow']",
    );

    const dateInputs = container.querySelectorAll("input[type='date']");
    dateInputs.forEach((input) => {
      input.required = rows.length > 0;
    });

    const timeInputs = container.querySelectorAll("input[type='time']");
    timeInputs.forEach((input) => {
      input.required = rows.length > 0;
    });
  }

  contactTemplate() {
    return `
      <div class="space-y-2 mt-2" data-add-fields-target="fieldRow">
        <div class="flex gap-2 w-full items-center">
          <input type="text" name="event[contact][][name]" placeholder="Name"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:ring-blue-500 sm:text-sm">

          <input type="text" name="event[contact][][email]" placeholder="Email"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:ring-blue-500 sm:text-sm">

          <input type="text" name="event[contact][][phone]" placeholder="Phone"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:ring-blue-500 sm:text-sm">

          <i class="far fa-circle-xmark text-bw-500 flex items-center cursor-pointer ml-2 mt-2"
            data-action="click->add-fields#removeField"></i>
        </div>
      </div>
    `;
  }

  conferenceDatesTemplate() {
    return `
      <div class="space-y-2 mt-2" data-add-fields-target="fieldRow">
        <div class="flex gap-2 w-full items-center">
          <input type="date" name="event[conference_dates][][date]" placeholder="Date"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:ring-blue-500 sm:text-sm">

          <div class="flex items-center gap-2 w-full">
            <input type="time" name="event[conference_dates][][start_time]"
              class="text-sm border border-gray-300 rounded-md py-2 px-2 bg-white w-full focus:z-10 focus:ring-2 focus:ring-blue-500">
            <span class="text-gray-500 font-medium text-sm">to</span>
            <input type="time" name="event[conference_dates][][end_time]" placeholder="End Time"
              class="text-sm border border-gray-300 rounded-md py-2 px-2 bg-white w-full focus:z-10 focus:ring-2 focus:ring-blue-500">
          </div>

          <input type="text" name="event[conference_dates][][notes]" placeholder="Notes"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:ring-blue-500 sm:text-sm">

          <i class="far fa-circle-xmark text-bw-500 flex items-center cursor-pointer ml-2 mt-2"
            data-action="click->add-fields#removeField"></i>
        </div>
      </div>
    `;
  }

  exhibitingDatesTemplate() {
    return `
      <div class="space-y-2 mt-2" data-add-fields-target="fieldRow">
        <div class="flex gap-2 w-full items-center">
          <input type="date" name="exhibiting_detail[exhibiting_dates][][date]" placeholder="Date"
            class="text-sm border border-gray-300 rounded-md py-2 px-2 bg-white w-full">

          <div class="flex items-center gap-2 w-full">
            <input type="time" name="exhibiting_detail[exhibiting_dates][][start_time]"
              class="text-sm border border-gray-300 rounded-md py-2 px-2 bg-white w-full focus:z-10 focus:ring-2 focus:ring-blue-500">
            <span class="text-gray-500 font-medium text-sm">to</span>
            <input type="time" name="exhibiting_detail[exhibiting_dates][][end_time]"
              class="text-sm border border-gray-300 rounded-md py-2 px-2 bg-white w-full focus:z-10 focus:ring-2 focus:ring-blue-500">
          </div>

          <input type="text" name="exhibiting_detail[exhibiting_dates][][notes]" placeholder="Notes"
            class="text-sm border border-gray-300 rounded-md py-2 px-2 bg-white w-full">

          <i class="far fa-circle-xmark text-gray-500 cursor-pointer ml-2"
            data-action="click->add-fields#removeField"></i>
        </div>
      </div>
    `;
  }

  additionalPropertyTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2 md:col-span-2">
            <label class="block text-sm font-medium text-gray-700">What is the address?</label>
            <input type="text" name="personal_information[additional_properties][][address]" placeholder="Full address"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Who owns the property?</label>
            <select name="personal_information[additional_properties][][ownership]"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
              <option value="">Select</option>
              <option value="joint">You & Spouse</option>
              <option value="llc">LLC</option>
              <option value="trust">Trust</option>
              <option value="landlord">Landlord</option>
            </select>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">What year did you purchase?</label>
            <input type="number" name="personal_information[additional_properties][][purchase_year]" placeholder="YYYY"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Estimated value</label>
            <input type="text" name="personal_information[additional_properties][][value]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Amount owed</label>
            <input type="text" name="personal_information[additional_properties][][owed]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Is it rented?</label>
            <select name="personal_information[additional_properties][][rented]"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Rent amount (if applicable)</label>
            <input type="text" name="personal_information[additional_properties][][rent_amount]" placeholder="$ per month"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Property
          </button>
        </div>
      </div>
    `;
  }

  highValuePropertyTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">What is it?</label>
            <input type="text" name="personal_information[high_value_property_items][][name]" placeholder="e.g. Antique furniture"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Ownership</label>
            <input type="text" name="personal_information[high_value_property_items][][ownership]" placeholder="Owner name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Estimated value</label>
            <input type="text" name="personal_information[high_value_property_items][][value]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Item
          </button>
        </div>
      </div>
    `;
  }

  bankAccountTemplate(type) {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Name on account</label>
            <input type="text" name="personal_information[${type}_accounts][][name_on_account]" placeholder="Account holder name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Typical balance (ballpark figure)</label>
            <input type="text" name="personal_information[${type}_accounts][][balance]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Account
          </button>
        </div>
      </div>
    `;
  }

  nonRetirementInvestmentTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">What kind of account?</label>
            <input type="text" name="personal_information[non_retirement_investments][][account_type]" placeholder="e.g. Stocks, Bonds"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">How much is it worth?</label>
            <input type="text" name="personal_information[non_retirement_investments][][value]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Whose name is on it?</label>
            <input type="text" name="personal_information[non_retirement_investments][][name]" placeholder="Owner name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Investment
          </button>
        </div>
      </div>
    `;
  }

  retirementPlanTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Ownership of the account</label>
            <input type="text" name="personal_information[retirement_plans][][ownership]" placeholder="Owner name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Type (Roth/Simple/Traditional)</label>
            <input type="text" name="personal_information[retirement_plans][][type]" placeholder="e.g. Roth IRA"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">How much is it worth?</label>
            <input type="text" name="personal_information[retirement_plans][][value]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Retirement Plan
          </button>
        </div>
      </div>
    `;
  }

  lifeInsuranceTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Ownership</label>
            <input type="text" name="personal_information[life_insurance][][ownership]" placeholder="Owner name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Type (term/whole/variable)</label>
            <input type="text" name="personal_information[life_insurance][][type]" placeholder="e.g. Term"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Death benefit</label>
            <input type="text" name="personal_information[life_insurance][][death_benefit]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Life Insurance
          </button>
        </div>
      </div>
    `;
  }

  highRiskVehicleTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Type (RV, boat, ATV, motorcycle, etc.)</label>
            <input type="text" name="personal_information[high_risk_vehicles][][type]" placeholder="e.g. RV"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Estimated value</label>
            <input type="text" name="personal_information[high_risk_vehicles][][value]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Amount owed</label>
            <input type="text" name="personal_information[high_risk_vehicles][][owed]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Ownership</label>
            <input type="text" name="personal_information[high_risk_vehicles][][ownership]" placeholder="Owner name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Vehicle
          </button>
        </div>
      </div>
    `;
  }

  personalVehicleTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Year, make and model</label>
            <input type="text" name="personal_information[personal_vehicles][][year_make_model]" placeholder="e.g. 2020 Toyota Camry"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Whose name is on the vehicle?</label>
            <input type="text" name="personal_information[personal_vehicles][][name]" placeholder="Owner name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Estimated value</label>
            <input type="text" name="personal_information[personal_vehicles][][value]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Amount owed</label>
            <input type="text" name="personal_information[personal_vehicles][][owed]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Vehicle
          </button>
        </div>
      </div>
    `;
  }

  inviteeTemplate() {
    const timestamp = Date.now();

    return `
      <div class="flex items-center gap-2 mb-3" data-add-fields-target="fieldRow">
        <input type="email" name="event[invitees_attributes][${timestamp}][email]" placeholder="johndoe@example.com" class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400">
        <i class="far fa-circle-xmark text-bw-500 cursor-pointer ml-2 mt-2"
          data-action="click->add-fields#removeField"></i>
      </div>
    `;
  }

  businessOwnershipTemplate() {
    return `
      <div class="space-y-2 mt-4 p-4 border border-gray-200 rounded-lg bg-white" data-add-fields-target="fieldRow">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-2 md:col-span-2">
            <label class="block text-sm font-medium text-gray-700">Legal name of the business</label>
            <input type="text" name="personal_information[business_ownership][][legal_name]" placeholder="Business legal name"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Taxed as (C-Corp, S-Corp, etc.)</label>
            <input type="text" name="personal_information[business_ownership][][tax_status]" placeholder="e.g. S-Corp"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Ownership percentage</label>
            <input type="text" name="personal_information[business_ownership][][ownership_percent]" placeholder="e.g. 50%"
              data-controller="input-masking" data-input-masking-mask-value="percentage"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">State filed in</label>
            <input type="text" name="personal_information[business_ownership][][state]" placeholder="e.g. California"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">Gross revenue</label>
            <input type="text" name="personal_information[business_ownership][][revenue]" placeholder="$"
              data-controller="input-masking" data-input-masking-mask-value="dollars"
              class="w-full bg-white px-2 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">
          </div>
        </div>

        <div class="flex justify-end mt-2">
          <button type="button" class="text-red-600 hover:text-red-800 text-sm font-medium"
            data-action="click->add-fields#removeField">
            <i class="far fa-circle-xmark mr-1"></i> Remove Business
          </button>
        </div>
      </div>
    `;
  }

  previousEventDataTemplate() {
    return `
      <div class="space-y-2 mt-2" data-add-fields-target="fieldRow">
        <div class="flex gap-2 w-full items-center">
          <input type="date"
            name="event[previous_event_data][][year]"
            placeholder="Year"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">

          <input type="text"
            name="event[previous_event_data][][speaker]"
            placeholder="Speaker"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">

          <input type="text"
            name="event[previous_event_data][][sales]"
            placeholder="Sales"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">

          <input type="text"
            name="event[previous_event_data][][profit_and_loss]"
            placeholder="Profit / Loss Amount"
            class="w-full bg-white py-2 px-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm">

          <i class="far fa-circle-xmark text-bw-500 flex items-center cursor-pointer ml-2 mt-2"
            data-action="click->add-fields#removeField"></i>
        </div>
      </div>
    `;
  }

  eventAstTemplate() {
    const timestamp = Date.now();
    return `
      <div class="space-y-2 mt-2" data-add-fields-target="fieldRow">
        <div class="flex flex-col gap-2 w-full" data-add-fields-target="fieldRowContent">
          <select
            name="event[event_asts_attributes][${timestamp}][user_id]"
            class="flex-1 w-full"
            data-controller="tom-select"
          >
            <option value="">Select AST</option>
            ${this.astOptions()}
          </select>
          <input
            type="text"
            name="event[event_asts_attributes][${timestamp}][ast_days]"
            placeholder="AST Days (e.g. Day 1-3)"
            class="flex-1 w-full bg-white px-2 py-2 rounded-md border border-gray-300"
          />
          <input
            type="hidden"
            name="event[event_asts_attributes][${timestamp}][_destroy]"
            value="false"
          />
        </div>
        <i class="far fa-circle-xmark cursor-pointer ml-auto mt-2 self-center" data-action="click->add-fields#removeField"></i>
      </div>
    `;
  }

  astOptions() {
    const select = document.querySelector(
      "[data-add-fields-container-type='event_asts'] select",
    );

    if (!select) return "";
    const optionsArray = Array.from(select.options);

    return optionsArray
      .filter((option) => option.value)
      .map(
        (option) => `<option value="${option.value}">${option.text}</option>`,
      )
      .join("");
  }
}
